import React from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faCalendarDays,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import {
  faAlarmClock,
  faChevronDoubleLeft,
  faHourglassClock,
  faTrashCheck,
} from "@fortawesome/pro-solid-svg-icons";
import { faXmark } from "@fortawesome/pro-light-svg-icons";
import { status } from "shared/src/appointment.mjs";
import * as api from "../../services/api";
import * as f from "../../utils/formatter";
import ServiceTag from "../ServiceTag";
import IconAngry from "../icons/IconAngry.jsx";
import IconLanguage from "../icons/IconLanguage.jsx";
import IconSeat from "../icons/IconSeat.jsx";
import IconNotes from "../icons/IconNotes.jsx";
import IconIssue from "../icons/IconIssue.jsx";
import Link from "../Link";
import { rawWaitingTimes } from "../calendar";
import { getForeignSpeakingOnlyTitle, hasBlocker } from "shared/src/client.mjs";
import { FileLink } from "../File";
import { useClientProblem } from "../client/hooks/useClientProblem.jsx";
import { useRoles } from "../../context/AuthenticationTokenContext.jsx";
import { role } from "shared/src/roles.mjs";
import { formData, FormErrorList, useFormAction } from "../../form";
import { useRevalidator } from "react-router";
import ButtonLoader from "../ButtonLoader";

function canRescheduleAppointment(appointment) {
  const availableProductIds = new Set(
    appointment?.client?.activeTreatments?.map((t) => t.product_id) ?? [],
  );

  const currentProductId = appointment?.service?.product_id;

  return availableProductIds.has(currentProductId);
}

function NextAppointmentNoteForm({ close, client_id }) {
  const revalidator = useRevalidator();

  const { onSubmit, loading, errors } = useFormAction(
    (e) => api.createNextAppointmentNote({ input: formData(e.currentTarget) }),
    () => {
      revalidator.revalidate();
      close();
    },
  );

  return (
    <form onSubmit={onSubmit}>
      <input type="hidden" name="client_id" value={client_id} />
      <input type="hidden" name="role" value={role.Front_Office} />
      <Box sx={{ display: "flex", gap: "1rem", flexDirection: "column" }}>
        <span>Poznámka k termínu:</span>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "1rem",
            alignItems: "center",
          }}
        >
          <TextField
            multiline
            rows={3}
            sx={{ flexGrow: 1 }}
            placeholder="Zde můžete napsat zprávu"
            name="note"
            required
          />
          <FormErrorList formErrors={errors} />
          <Box>
            <Button variant="contained" type="submit" disabled={loading}>
              <FontAwesomeIcon
                icon={faArrowRight}
                style={{ marginRight: "8px" }}
              />
              {loading && <ButtonLoader />}
              Odeslat
            </Button>
          </Box>
        </Box>
      </Box>
    </form>
  );
}

/**
 * An appointment detail modal.
 *
 * @typedef {object} AppointmentDetailModalProps
 * @property {Appointment} appointment
 * @property {function} handleOpenConfirmationModal Function to open confirmation modal
 * @property {function} handleCloseAppointmentDetailModal Function to close appointment detail modal
 * @property {boolean} openAppointmentDetailModal Flag to open appointment detail modal
 * @property {number} checkedInMinutes Number of minutes checked in
 * @property {number} waitingMinutes Number of minutes waiting
 *
 * @param {AppointmentDetailModalProps} props
 * @returns {JSX.Element}
 */

export const AppointmentDetailModal = ({
  appointment,
  handleOpenConfirmationModal,
  handleCloseAppointmentDetailModal,
}) => {
  const { t } = useTranslation();
  const roles = useRoles();
  const problem = useClientProblem(appointment.client, roles);
  const languages = getForeignSpeakingOnlyTitle(appointment.client);

  const waitingTimes = rawWaitingTimes(appointment, new Date());
  const checkedInMinutes = waitingTimes?.checked_in_minutes;
  const waitingMinutes = waitingTimes?.waiting_minutes;

  const handleMoveAppointment = (status) => {
    handleCloseAppointmentDetailModal();
    api.patchAppointment({
      id: appointment.id,
      status_id: status,
    });
  };

  return (
    <Dialog open onClose={handleCloseAppointmentDetailModal}>
      <DialogTitle
        sx={{
          padding: "2.4rem 2rem",
          margin: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {f.fullName(appointment.client)}
        <IconButton
          aria-label="close"
          onClick={handleCloseAppointmentDetailModal}
        >
          <FontAwesomeIcon icon={faXmark} fixedWidth />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "1.5rem",
          padding: "0 3.8rem",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: "10px",
          }}
        >
          <IconNotes appointment={appointment} />

          {languages != null && (
            <Tooltip title={languages}>
              <IconLanguage />
            </Tooltip>
          )}

          {problem != null && (
            <Tooltip title={problem}>
              <IconAngry />
            </Tooltip>
          )}
        </Box>
        <ServiceTag service={appointment.service} />
        {hasBlocker(appointment.client) && (
          <Box>
            <IconIssue />
            {appointment.client?.blockers?.map((blocker, i) => {
              return (
                <Box key={i}>
                  <Typography>{blocker.text}</Typography>
                  {blocker.file != null && (
                    <FileLink
                      fileInfo={blocker.file}
                      details={blocker.file.details}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        )}
        <Box>
          <strong style={{ fontSize: "1.3rem" }}>
            {t("reception.appointment_start")}:
          </strong>{" "}
          {f.time24(appointment.start)}
        </Box>
        {appointment.status_id === status.ASSIGNED && (
          <Box
            sx={{
              color: "primary.main",
              fontWeight: "bold",
              fontSize: "1.3rem",
            }}
          >
            <IconSeat /> {t("reception.seat_preparing")}
          </Box>
        )}
        <Box style={{ display: "flex", gap: "2rem" }}>
          {waitingMinutes != undefined && (
            <Box>
              <FontAwesomeIcon
                icon={faAlarmClock}
                style={{ marginRight: "5px" }}
              />
              <strong style={{ fontSize: "1.3rem" }}>
                {t("reception.waiting")}
              </strong>
              :{" "}
              <span
                style={{
                  color:
                    waitingMinutes > 0
                      ? "var(--mui-palette-_components-alert-error-color)"
                      : "var(--mui-palette-_components-alert-info-color)",
                }}
              >
                {f.absoluteMinutesToDuration(waitingMinutes)}
              </span>
            </Box>
          )}
          {checkedInMinutes != undefined && (
            <Box>
              <FontAwesomeIcon
                icon={faHourglassClock}
                style={{ marginRight: "5px" }}
              />
              <strong style={{ fontSize: "1.3rem" }}>
                {t("reception.arrival")}
              </strong>
              :{" "}
              <span
                style={{
                  color:
                    checkedInMinutes > 0
                      ? "var(--mui-palette-_components-alert-error-color)"
                      : "var(--mui-palette-_components-alert-info-color)",
                }}
              >
                {f.absoluteMinutesToDuration(checkedInMinutes)}
              </span>
            </Box>
          )}
        </Box>
        {(appointment.status_id === status.BOOKED ||
          appointment.status_id === status.CONFIRMED ||
          appointment.status_id === status.WAITING) && (
          <Box sx={{ display: "flex", gap: "1rem" }}>
            {canRescheduleAppointment(appointment) && (
              <Link
                to={`/clients/${appointment.client.id}?reschedule=${appointment.parent_id ?? appointment.id}`}
              >
                <Button variant="outlined" style={{ fontSize: "1.4rem" }}>
                  <FontAwesomeIcon
                    icon={faCalendarDays}
                    style={{ marginRight: "8px" }}
                  />
                  Přeobjednat
                </Button>
              </Link>
            )}
            <Link
              to={`/clients/${appointment.client.id}?delete=${appointment.parent_id ?? appointment.id}`}
            >
              <Button variant="outlined">
                <FontAwesomeIcon
                  icon={faTrashCheck}
                  style={{ marginRight: "8px" }}
                />
                Zrušit termín
              </Button>
            </Link>
          </Box>
        )}
        <NextAppointmentNoteForm
          close={handleCloseAppointmentDetailModal}
          client_id={appointment.client.id}
        />
      </DialogContent>

      <DialogActions sx={{ padding: "3.5rem 2rem 3rem 2rem" }}>
        <Box>
          {appointment.status_id === status.CONFIRMED && (
            <Button
              variant="contained"
              onClick={() => handleMoveAppointment(status.WAITING)}
              style={{ fontSize: "1.4rem" }}
            >
              <FontAwesomeIcon icon={faCheck} style={{ marginRight: "8px" }} />
              {t("reception.resolved")}
            </Button>
          )}
        </Box>
        <Button
          color="secondary"
          variant="contained"
          onClick={() => {
            handleOpenConfirmationModal();
          }}
        >
          <FontAwesomeIcon
            icon={faChevronDoubleLeft}
            style={{ marginRight: "8px" }}
          />
          {t("reception.return_to_booked")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
